import React, { useState } from 'react';
import { useAuth } from '../../stateManagers/services/AuthContext';

const LoginScreen = () => {
  const { login, error, clearError, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) {
      return;
    }
    
    setIsLoggingIn(true);
    clearError();
    
    try {
      await login(email, password);
    } catch (err) {
      console.error('Login failed:', err);
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#e5e5e5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        display: 'flex',
        width: '100%',
        maxWidth: '80rem',
        minHeight: '600px',
        background: 'white',
        borderRadius: '24px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Left Panel - Gradient with Message */}
        <div style={{
          flex: 1,
          background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #d4f17e 100%)',
          padding: '60px 50px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative blur circles */}
          <div style={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '300px',
            height: '300px',
            background: 'rgba(212, 241, 126, 0.2)',
            borderRadius: '50%',
            filter: 'blur(60px)'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-150px',
            left: '-100px',
            width: '400px',
            height: '400px',
            background: 'rgba(15, 61, 62, 0.3)',
            borderRadius: '50%',
            filter: 'blur(80px)'
          }} />

          {/* Logo */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: '#d4f17e',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="pi pi-th-large" style={{ fontSize: '1.4rem', color: '#0f3d3e' }}></i>
            </div>
            <span style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: 'white',
              letterSpacing: '-0.5px'
            }}>
              HCI Main
            </span>
          </div>

          {/* Main Message */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <p style={{
              fontSize: '1rem',
              color: 'rgba(255, 255, 255, 0.8)',
              marginBottom: '16px',
              fontWeight: '400'
            }}>
              You can easily
            </p>
            <h1 style={{
              fontSize: '2.5rem',
              color: 'white',
              margin: 0,
              fontWeight: '600',
              lineHeight: '1.2',
              letterSpacing: '-1px'
            }}>
              Manage your HCI with clarity and efficiency.
            </h1>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div style={{
          flex: 1,
          padding: '60px 50px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: 'white'
        }}>
          {/* Icon */}
          <div style={{
            width: '48px',
            height: '48px',
            background: '#d4f17e',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px'
          }}>
            <i className="pi pi-sign-in" style={{ fontSize: '1.3rem', color: '#0f3d3e' }}></i>
          </div>

          {/* Title */}
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#1a1a1a',
            margin: '0 0 8px 0',
            letterSpacing: '-0.5px'
          }}>
            Welcome back
          </h2>
          <p style={{
            fontSize: '0.95rem',
            color: '#666',
            margin: '0 0 32px 0'
          }}>
            Access your dashboard and manage everything in one place.
          </p>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '14px 16px',
              background: '#fee',
              border: '1px solid #fcc',
              borderRadius: '12px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <i className="pi pi-exclamation-circle" style={{ color: '#c00', fontSize: '1.2rem' }}></i>
              <span style={{ color: '#c00', fontSize: '0.9rem', flex: 1 }}>{error}</span>
              <button
                onClick={clearError}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#c00',
                  padding: '4px'
                }}
              >
                <i className="pi pi-times" style={{ fontSize: '1rem' }}></i>
              </button>
            </div>
          )}

          {/* Email Input */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.9rem',
              fontWeight: '500',
              color: '#333',
              marginBottom: '8px'
            }}>
              Your email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@hciindia.in"
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '1rem',
                border: '2px solid #e0e0e0',
                borderRadius: '12px',
                outline: 'none',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1a5555';
                e.target.style.boxShadow = '0 0 0 3px rgba(26, 85, 85, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e0e0e0';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Password Input */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.9rem',
              fontWeight: '500',
              color: '#333',
              marginBottom: '8px'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                style={{
                  width: '100%',
                  padding: '14px 50px 14px 16px',
                  fontSize: '1rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1a5555';
                  e.target.style.boxShadow = '0 0 0 3px rgba(26, 85, 85, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.boxShadow = 'none';
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleLogin();
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className={`pi ${showPassword ? 'pi-eye-slash' : 'pi-eye'}`} style={{ fontSize: '1.1rem' }}></i>
              </button>
            </div>
          </div>

          {/* Login Button */}
          <button
            onClick={handleLogin}
            disabled={isLoggingIn || !email || !password}
            style={{
              width: '100%',
              padding: '16px',
              background: (isLoggingIn || !email || !password) ? '#ccc' : '#0f3d3e',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: (isLoggingIn || !email || !password) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (!isLoggingIn && email && password) {
                e.currentTarget.style.background = '#1a5555';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 61, 62, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoggingIn && email && password) {
                e.currentTarget.style.background = '#0f3d3e';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            {isLoggingIn ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
                Signing in...
              </>
            ) : (
              'Sign in'
            )}
          </button>

          {/* Divider */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            margin: '24px 0',
            color: '#999',
            fontSize: '0.85rem'
          }}>
            <div style={{ flex: 1, height: '1px', background: '#e0e0e0' }} />
            <span style={{ padding: '0 16px' }}>or continue with</span>
            <div style={{ flex: 1, height: '1px', background: '#e0e0e0' }} />
          </div>

          {/* Social Login Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px'
          }}>
            {['google', 'github', 'apple'].map((provider) => (
              <button
                key={provider}
                type="button"
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'white',
                  border: '2px solid #e0e0e0',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#1a5555';
                  e.currentTarget.style.background = '#f8f8f8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e0e0e0';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <i className={`pi ${
                  provider === 'google' ? 'pi-google' : 
                  provider === 'github' ? 'pi-github' : 
                  'pi-apple'
                }`} style={{ fontSize: '1.2rem', color: '#333' }}></i>
              </button>
            ))}
          </div>

          {/* Footer Link */}
          <p style={{
            textAlign: 'center',
            fontSize: '0.9rem',
            color: '#666',
            margin: 0
          }}>
            Don't have an account?{' '}
            <a href="#" style={{
              color: '#1a5555',
              textDecoration: 'none',
              fontWeight: '600',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#d4f17e'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#1a5555'}
            >
              Contact Admin
            </a>
          </p>
        </div>
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          div[style*="maxWidth: '1000px'"] > div:first-child {
            display: none;
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LoginScreen;